/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import InputAdornment from '@mui/material/InputAdornment';
import DeleteIcon from '@mui/icons-material/Delete';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as SidebarUserIcon } from '../assets/icons/sidebar_user_settings.svg';
import PageHeader from '../components/PageHeader';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { useAuth } from '../auth/AuthContext';
import api from '../services/api';
import { IdentityUser } from '../types/index';

// InitialSetupModal と共通のフィールドスタイル
const normalFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F5F5F5',
    borderRadius: '12px',
    '& fieldset': { border: 'none' },
    '& input': { color: '#212121', fontSize: '15px' },
    '& input::placeholder': { color: '#BDBDBD', opacity: 1 },
  },
};

const MAX_USERS = 3;

const validateEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const UserSettingsPage: React.FC = () => {
  const { storeId } = useAuth();

  const [users, setUsers] = useState<IdentityUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const scrollToField = (fieldName: string) => {
    setTimeout(() => {
      const el = document.querySelector(`[name="${fieldName}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el as HTMLElement).focus();
      }
    }, 100);
  };

  // 新規追加用の入力欄（名前＋メールアドレス）
  interface NewUserInput { name: string; email: string; }
  const [newUsers, setNewUsers] = useState<NewUserInput[]>([]);
  const [isAddingUser, setIsAddingUser] = useState(false);

  const [userToDelete, setUserToDelete] = useState<IdentityUser | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  const fetchUsers = async () => {
    if (!storeId) return;
    setIsLoading(true);
    try {
      const data = await api.adminIdentityUser.getStoreUsers(storeId);
      setUsers(data);
      if (data.length === 0) {
        setNewUsers([{ name: '', email: '' }]);
      }
    } catch {
      setError('ユーザー一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [storeId]);

  // ── 新規入力欄の操作 ──────────────────────────────────────────

  const addUserInput = () => {
    const totalAfterAdd = users.length + newUsers.length + 1;
    if (totalAfterAdd <= MAX_USERS) {
      setNewUsers(prev => [...prev, { name: '', email: '' }]);
    }
  };

  const removeUserInput = (index: number) => {
    setNewUsers(prev => prev.filter((_, i) => i !== index));
  };

  const updateUserInput = (index: number, field: 'name' | 'email', value: string) => {
    setNewUsers(prev => prev.map((v, i) => i === index ? { ...v, [field]: value } : v));
    const errorKey = `${field}_${index}`;
    if (fieldErrors[errorKey]) {
      setFieldErrors(prev => { const next = { ...prev }; delete next[errorKey]; return next; });
    }
  };

  // ── ユーザー追加（有効なメールアドレスをまとめて登録） ────────────

  const handleAddUsers = async () => {
    const errors: Record<string, string> = {};

    // Check if at least one email entered
    const validUsers = newUsers.filter(u => u.email.trim());
    if (validUsers.length === 0) {
      errors.email_0 = 'メールアドレスを入力してください';
    }

    // 上限チェック
    if (users.length + validUsers.length > MAX_USERS) {
      setError(`ユーザーは最大${MAX_USERS}名までです（現在${users.length}名登録済み）`);
      return;
    }

    // Validate each new user
    newUsers.forEach((u, i) => {
      if (u.email.trim() || u.name.trim()) { // Only validate rows that have some input
        if (!u.name.trim()) errors[`name_${i}`] = '管理者名を入力してください';
        if (!u.email.trim()) {
          errors[`email_${i}`] = 'メールアドレスを入力してください';
        } else if (!validateEmail(u.email.trim())) {
          errors[`email_${i}`] = '無効なメールアドレスです';
        }
      }
    });

    // Duplicate check
    const emails = validUsers.map(u => u.email.trim());
    if (new Set(emails).size !== emails.length) {
      const seen = new Set<string>();
      validUsers.forEach((u, i) => {
        if (seen.has(u.email.trim())) {
          errors[`email_${i}`] = '同じメールアドレスが含まれています';
        }
        seen.add(u.email.trim());
      });
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      scrollToField(Object.keys(errors)[0]);
      return;
    }
    setFieldErrors({});

    setIsAddingUser(true);
    setError(null);
    try {
      const created: IdentityUser[] = [];
      for (const u of validUsers) {
        const user = await api.adminIdentityUser.create({
          email: u.email.trim(),
          displayName: u.name.trim(),
          storeId: storeId!,
        });
        created.push(user);
      }
      setNewUsers([{ name: '', email: '' }]);
      setSuccessMessage(`${created.length}名のユーザーを追加しました。パスワード設定用のメールを送信しました。`);
      await fetchUsers();
    } catch (err: any) {
      if (err.message?.includes('already exists') || err.message?.includes('EMAIL_EXISTS')) {
        setError('登録済みのメールアドレスが含まれています');
      } else {
        setError('ユーザーの追加に失敗しました');
      }
    } finally {
      setIsAddingUser(false);
    }
  };

  // ── ユーザー削除 ─────────────────────────────────────────────────

  const handleDeleteUser = async () => {
    if (!userToDelete || !storeId) return;
    setIsDeletingUser(true);
    try {
      await api.adminIdentityUser.delete(userToDelete.uid, storeId);
      setUserToDelete(null);
      setSuccessMessage('ユーザーを削除しました');
      await fetchUsers();
    } catch {
      setError('ユーザーの削除に失敗しました');
    } finally {
      setIsDeletingUser(false);
    }
  };

  const canAddMore = users.length + newUsers.length < MAX_USERS;
  const totalCount = users.length + newUsers.length;
  const atLimit = users.length >= MAX_USERS;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f8f9' }}>
      <PageHeader
        title="ユーザー設定"
        icon={<SvgIcon component={SidebarUserIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
      />

      {/* コンテンツ（InitialSetupModal の Step2Content と同じ構造） */}
      <Box sx={{ bgcolor: '#FFFFFF', px: 2.5, pb: 2.5 }}>
        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography sx={{ fontSize: '13px', color: '#616161', mb: 2.5, textAlign: 'center' }}>
          最低1名、最大3名のユーザー登録が必要です
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* 既存ユーザー（読み取り専用フィールドとして表示） */}
            <Stack spacing={2} sx={{ mb: !atLimit && newUsers.length > 0 ? 2 : 0 }}>
              {users.map((user, index) => (
                <Box key={user.uid}>
                  <Typography sx={{ fontSize: '13px', color: '#424242', mb: 0.75 }}>
                    管理者{totalCount > 1 ? `　${index + 1}` : ''}
                  </Typography>
                  {user.displayName && (
                    <Typography sx={{ fontSize: '14px', color: '#212121', mb: 0.5, pl: 0.5 }}>
                      {user.displayName}
                    </Typography>
                  )}
                  <TextField
                    value={user.email}
                    fullWidth
                    size="small"
                    disabled={false}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            color="error"
                            onClick={() => setUserToDelete(user)}
                            edge="end"
                            disabled={users.length <= 1 && newUsers.filter(u => u.email.trim()).length === 0}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={normalFieldSx}
                  />
                </Box>
              ))}

              {/* 新規追加入力欄（上限に達していない場合のみ表示） */}
              {!atLimit && newUsers.map((userInput, index) => (
                <Box key={index}>
                  <Typography sx={{ fontSize: '13px', color: '#424242', mb: 0.75 }}>
                    管理者{totalCount > 1 ? `　${users.length + index + 1}` : ''}
                  </Typography>
                  <TextField
                    name={`name_${index}`}
                    value={userInput.name}
                    onChange={e => updateUserInput(index, 'name', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="管理者名を入力"
                    error={!!fieldErrors[`name_${index}`]}
                    helperText={fieldErrors[`name_${index}`]}
                    sx={{ ...normalFieldSx, mb: 1 }}
                  />
                  <TextField
                    name={`email_${index}`}
                    type="email"
                    value={userInput.email}
                    onChange={e => updateUserInput(index, 'email', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="example@email.com"
                    error={!!fieldErrors[`email_${index}`]}
                    helperText={fieldErrors[`email_${index}`]}
                    sx={normalFieldSx}
                    InputProps={newUsers.length > 1 || users.length > 0 ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            color="error"
                            onClick={() => removeUserInput(index)}
                            edge="end"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    } : undefined}
                  />
                </Box>
              ))}
            </Stack>

            {/* IDを追加ボタン（上限未満の場合のみ表示） */}
            {canAddMore && !atLimit && (
              <Box sx={{ mt: 1 }}>
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={addUserInput}
                  sx={{
                    bgcolor: '#E1F5FE',
                    color: '#1976D2',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    px: 2.5,
                    py: 0.5,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#B3E5FC' },
                  }}
                >
                  IDを追加
                </Button>
              </Box>
            )}

            {/* 登録ボタン（新規入力がある場合のみ表示） */}
            {!atLimit && newUsers.some(u => u.email.trim()) && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleAddUsers}
                  disabled={isAddingUser}
                  startIcon={isAddingUser ? <CircularProgress size={20} color="inherit" /> : null}
                  fullWidth
                  sx={{
                    bgcolor: '#1565C0',
                    borderRadius: '12px',
                    height: 52,
                    fontSize: '16px',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#0D47A1' },
                    '&.Mui-disabled': { bgcolor: '#90A4AE', color: 'white' },
                  }}
                >
                  {isAddingUser ? '登録処理中...' : '登録する'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog open={!!userToDelete} onClose={() => setUserToDelete(null)}>
        <DialogTitle>ユーザーの削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {userToDelete?.email} を削除しますか？この操作は元に戻せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserToDelete(null)} disabled={isDeletingUser}>
            キャンセル
          </Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
            disabled={isDeletingUser}
            startIcon={isDeletingUser ? <CircularProgress size={16} /> : undefined}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserSettingsPage;
